import { useI18n } from "@core/i18n/i18n";
import { programOperationsManager } from "@core/service-container";
import { PatientProgramEditor } from "@domain/program/patient-program";
// ProgramId import removed - now using string type instead
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { ToastContext } from "@ui/components/common/toast-provider";
import { ConfirmActionDialog } from "@ui/pages/program-builder/dialog/confirm-dialog";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useContext, useState } from "react";

export interface StopProgramDialogProps extends DialogProps {
	patient: { id: string; analyticsId: string };
	program: { id: string };
	editor: PatientProgramEditor;
	onSuccess?: () => void;
}

export const StopProgramDialog = ({
	patient,
	program,
	editor,
	onSuccess,
	...otherProps
}: StopProgramDialogProps): JSX.Element => {
	const { format } = useI18n();
	const { showToast } = useContext(ToastContext);

	const [awaitingStop, setAwaitingStop] = useState(false);

	const stopProgram = useCallback(async () => {
		setAwaitingStop(true);
		try {
			const { status } = await programOperationsManager.performStopProgramOperation(patient.id, program.id, editor);
			setAwaitingStop(false);
			onSuccess?.();
			otherProps.onHide();
			showToast(
				format("global.stopProgramOperation.successToast.title"),
				format(
					status === ProgramStatus.Draft
						? "global.stopProgramOperation.draft.successToast.message"
						: "global.stopProgramOperation.successToast.message",
				),
				"success",
			);
		} catch (error) {
			console.error("Failed to stop program", error);
			setAwaitingStop(false);
			showToast(
				format("global.stopProgramOperation.errorToast.title"),
				format("global.stopProgramOperation.errorToast.message"),
			);
		}
	}, [editor, format, onSuccess, otherProps, patient.id, program.id, showToast]);

	return (
		<ConfirmActionDialog
			{...otherProps}
			onCancel={otherProps.onHide}
			onConfirm={stopProgram}
			confirmLoading={awaitingStop}
			title={format("programBuilder.stopDialog.header")}
			message={format("programBuilder.stopDialog.message")}
		/>
	);
};
